import { useDraggable } from "@dnd-kit/core";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";

import { currencyFormatLong } from "components/app_layout/Typography/Numbers";
import UserInitials from "components/ui/form_components/UserInitials";
import UserName from "components/users/UserName";
import { appRoutes } from "lib/appRoutes";
import { LeadFromSearch } from "lib/hooks/api/leads/useLeads";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { getDueDate } from "./utils";

export const CardShell = styled.div<{ dragging?: boolean }>(({ theme, dragging }) => ({
  background: theme.colors.sysBackgroundDefault,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 4,
  // 16px padding and row gap, per the Figma board spec (368px card, 336px content).
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  cursor: dragging ? "grabbing" : "grab",
  transition: "box-shadow 150ms ease-out, opacity 150ms ease-out",
  // While a drag is in flight the source card stays put and turns into a reserved
  // slot: dashed outline, faded contents, no shadow. It reads as a hole in the
  // column rather than a second copy of the card the pointer is carrying.
  ...(dragging && {
    opacity: 0.4,
    borderStyle: "dashed",
    borderColor: theme.colors.sysBorderFunctional,
    background: theme.colors.sysBackgroundAlternative,
    boxShadow: "none",
  }),
  ":hover": { boxShadow: dragging ? "none" : "0px 4px 10px rgba(0, 0, 0, 0.07)" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

const Title = styled.div(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.4,
  color: theme.colors.sysTextDefault,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const Row = styled.div<{ emphasis?: boolean; alert?: boolean }>(({ theme, emphasis, alert }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  fontSize: 13,
  fontWeight: emphasis ? 600 : 400,
  color: alert
    ? theme.colors.sysDestructiveDefault
    : emphasis
      ? theme.colors.sysTextDefault
      : theme.colors.sysTextSecondary,
}));

const Label = styled.span({ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" });

export function LeadCardBody({ lead }: { lead: LeadFromSearch }) {
  const buyerName = lead.buyers?.[0]?.name ?? "—";
  const owner = lead.assignedTo;
  const due = getDueDate(lead.expiryDate, new Date());

  return (
    <>
      <Title>{lead.name}</Title>
      <Row>
        <UIcon icon="wallet02" size={16} />
        <Label>{buyerName}</Label>
      </Row>
      <Row>
        {owner ? (
          <>
            <UserInitials
              firstName={owner.firstName}
              lastName={owner.lastName}
              showTooltip={false}
              type="extra-small"
            />
            <Label>
              <UserName user={owner} />
            </Label>
          </>
        ) : (
          <>
            <UIcon icon="userPlus01" size={16} color="sysTextPlaceholder" />
            <Label>Unassigned</Label>
          </>
        )}
      </Row>
      <Row emphasis data-testid="lead-card-value">
        <UIcon icon="currencyPoundCircle" size={16} />
        {/* Long format (£1,000,000) per the design, rather than the table's compact £1.0M. */}
        <Label>{currencyFormatLong(lead.value ?? null, "GBP")}</Label>
      </Row>
      {due && (
        <Row alert={due.soon} data-testid="lead-card-due" data-soon={String(due.soon)}>
          <UIcon icon="calendar" size={16} />
          <Label>{due.label}</Label>
        </Row>
      )}
    </>
  );
}

export function LeadCard({ lead }: { lead: LeadFromSearch }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead.id,
  });

  // The draggable's own transform is deliberately not applied: DragOverlay already
  // renders the card that follows the pointer, so moving this node too would drag
  // the ghost along underneath it instead of leaving it behind in the column.
  return (
    <CardShell
      ref={setNodeRef}
      dragging={isDragging}
      // A drag needs 8px of movement to start (see LeadsBoard sensors), so a
      // plain click still reads as a click and opens the lead.
      onClick={() => navigate(appRoutes.leadDetail(lead.id))}
      data-testid="lead-card"
      {...attributes}
      {...listeners}
    >
      <LeadCardBody lead={lead} />
    </CardShell>
  );
}
